import {httpsCallable} from "firebase/functions";
// @ts-ignore -- "FirebaseUtils" is resolved by the web bundler, not by tsc
import {functions} from "FirebaseUtils";
import LogService from "services/LogService.js";

/**
 * Adapty Web API (https://adapty.io/docs/web-api).
 *
 * Placement-driven paywall for the web onboarding flow: we fetch the paywall
 * for the onboarding placement to get its `variation_id` (so purchases and
 * views are attributed to the right paywall / A-B test), record the view, and
 * carry the variation id into the Paddle checkout. Rendering stays our own (Figma
 * TrialStep); the Web API does not return display prices.
 *
 * Auth uses the PUBLIC key (public_live_… / public_test_…), which is safe to
 * ship in client code — set REACT_APP_ADAPTY_PUBLIC_KEY. When it is absent the
 * helpers no-op so the flow keeps working without Adapty attribution.
 */

const ADAPTY_WEB_API_URL = "https://api.adapty.io/api/v2/web-api";
const ADAPTY_PUBLIC_KEY = process.env.REACT_APP_ADAPTY_PUBLIC_KEY;
// Placement for newly-created (onboarding) users — see the native PaywallPlacement enum
export const ADAPTY_ONBOARDING_PLACEMENT =
  process.env.REACT_APP_ADAPTY_ONBOARDING_PLACEMENT || "RemotePaywall_onboarding";
const STORE = "paddle";

// Mirrors the native PaywallPlacement enum (android .../paywall/PaywallPlacement.kt)
// so web and mobile resolve the same placement for the same user state.
export const ADAPTY_PLACEMENTS = {
  ultraActive: "RemotePaywall_active_ultra",
  proActive: "RemotePaywall_active",
  expired: "RemotePaywall_expired",
  onboarding: ADAPTY_ONBOARDING_PLACEMENT,
  free: "RemotePaywall"
};

/**
 * Picks the placement for a user, following the same rules as
 * PaywallPlacement.from() on Android.
 */
export const placementForUser = (subscription: any, accountAgeInDays = Number.MAX_SAFE_INTEGER): string => {
  const now = Date.now() / 1000;
  const premium = subscription?.premium;
  const ultra = subscription?.ultra;
  const isActive = (tier: any) => !!tier?.expiration && tier.expiration > now;

  if (isActive(ultra)) {
    return ADAPTY_PLACEMENTS.ultraActive;
  }
  if (isActive(premium)) {
    return ADAPTY_PLACEMENTS.proActive;
  }
  if (premium || ultra) {
    return ADAPTY_PLACEMENTS.expired;
  }
  return accountAgeInDays <= 30 ? ADAPTY_PLACEMENTS.onboarding : ADAPTY_PLACEMENTS.free;
};

/** Days since the Firebase Auth account was created; drives the paywall placement. */
export const accountAgeInDays = (authUser: any): number => {
  const createdAt = authUser?.metadata?.creationTime;
  if (!createdAt) {
    return Number.MAX_SAFE_INTEGER;
  }
  const created = new Date(createdAt).getTime();
  return Number.isNaN(created) ? Number.MAX_SAFE_INTEGER : Math.floor((Date.now() - created) / 86400000);
};

export interface OnboardingPaywall {
  variationId: string;
  placementId: string;
  vendorProductIds: string[];
}

const authHeaders = () => ({
  Authorization: `Api-Key ${ADAPTY_PUBLIC_KEY}`,
  "Content-Type": "application/json"
});

/**
 * Create/identify the Adapty profile for the current user (server-side, secret
 * key). getPaywall 404s ("Profile not found") for a web-first user until this
 * runs. Best-effort: returns false on failure and the caller still degrades
 * gracefully.
 */
export const ensureAdaptyProfile = async (): Promise<boolean> => {
  if (!ADAPTY_PUBLIC_KEY) {
    return false;
  }
  try {
    const callable = httpsCallable<Record<string, never>, {success: boolean}>(functions, "ensureAdaptyProfile");
    const result = await callable({});
    return !!result.data?.success;
  } catch (error: any) {
    LogService.addErrorLogAsync("", "ensureAdaptyProfile error: " + error.message, false, true);
    return false;
  }
};

/**
 * Fetch the onboarding paywall for this user. Returns null (and the caller
 * falls back to the default plans) when the public key is unset or the request
 * fails — a missing paywall must never block signup.
 */
export const fetchOnboardingPaywall = async (
  customerUserId: string,
  placementId: string = ADAPTY_ONBOARDING_PLACEMENT
): Promise<OnboardingPaywall | null> => {
  if (!ADAPTY_PUBLIC_KEY) {
    return null;
  }
  try {
    const response = await fetch(`${ADAPTY_WEB_API_URL}/paywall/`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        store: STORE,
        placement_id: placementId,
        customer_user_id: customerUserId
      })
    });
    if (!response.ok) {
      LogService.addErrorLogAsync("", `Adapty getPaywall failed: ${response.status}`, false, true);
      return null;
    }
    const body = await response.json();
    const paywall = body?.data ?? body;
    if (!paywall?.variation_id) {
      return null;
    }
    return {
      variationId: paywall.variation_id,
      placementId,
      vendorProductIds: (paywall.products ?? []).map((product: any) => product?.vendor_product_id).filter(Boolean)
    };
  } catch (error: any) {
    LogService.addErrorLogAsync("", "Adapty getPaywall error: " + error.message, false, true);
    return null;
  }
};

/** Best-effort conversion tracking; failures are swallowed. */
export const recordPaywallView = async (variationId: string, customerUserId: string): Promise<void> => {
  if (!ADAPTY_PUBLIC_KEY || !variationId) {
    return;
  }
  try {
    await fetch(`${ADAPTY_WEB_API_URL}/paywall/visit/`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        store: STORE,
        variation_id: variationId,
        customer_user_id: customerUserId
      })
    });
  } catch {
    // analytics only — never surface to the user
  }
};
